@if (available()) {
  <section class="p-8" aria-label="Version history">
    <h2 class="mb-4 text-sm font-semibold">Version history</h2>
    <p class="mb-8 rounded-[6px] border border-border bg-primary-50 px-7 py-6 text-sm leading-10 text-gray-700">
      Restoration creates a new version. Current ownership and access remain unchanged.
    </p>
    @if (failure()) {
      <p role="alert">{{ failure() }}</p>
      @if (!confirmation()) {
        <button type="button" [disabled]="busy()" (click)="retry()">Retry</button>
      }
    }
    @if (loading()) {
      <p role="status">Loading versions…</p>
    } @else if (!failure() && !rows().length) {
      <p>No versions yet</p>
    }
    <ul class="list-none p-0">
      @for (row of rows(); track row.version) {
        <li class="mb-8 rounded-[6px] border border-border p-8">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <strong class="text-sm">Version {{ row.version }}</strong>
                @if (row.version === current()) {
                  <strong class="rounded bg-primary-50 px-3 py-1 text-[11px] leading-7 font-medium text-primary-600">Current</strong>
                }
              </div>
              <p class="mt-3 text-xs leading-12 text-secondary-500">
                {{ row.author?.name ?? 'Former member' }} ·
                <time [attr.datetime]="row.at">{{ row.at | dateFormat: 'relativeDate' }}</time>
                @if (row.restoredFrom !== null) {
                  <span> · Restored from version {{ row.restoredFrom }}</span>
                }
                @if (row.fields.length) {
                  <span> · Changed: {{ changed(row) }}</span>
                }
                @if (row.review) {
                  <span> · {{ review(row) }}</span>
                }
              </p>
            </div>
            @if (canEdit() && row.version !== current()) {
              <button
                type="button"
                data-restore
                class="h-16 rounded-[6px] border border-border bg-background px-5 text-base leading-10 text-gray-700 disabled:cursor-default disabled:opacity-50"
                [disabled]="busy()"
                (click)="askRestore(row.version)"
              >
                Restore content
              </button>
            }
          </div>
          @if (row.version !== current()) {
            <details class="mt-6" (toggle)="compare(row.version, $event)">
              <summary class="cursor-pointer text-[11px] leading-8 text-primary-600">Compare with current version</summary>
              @switch (comparisonState(row.version)) {
                @case ('loading') {
                  <p role="status">Loading this version…</p>
                }
                @case ('failed') {
                  <p role="alert">{{ comparisonFailure(row.version) }}</p>
                }
                @case ('ready') {
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Field</th>
                        <th scope="col">Version {{ row.version }}</th>
                        <th scope="col">Current version</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (field of comparisonRows(row.version); track field.label) {
                        <tr>
                          <th scope="row">{{ field.label }}</th>
                          @if (field.kind === 'date') {
                            <td>
                              {{ field.saved ? (field.saved | dateFormat: 'date' : undefined : true : 'UTC') : '—' }}
                            </td>
                            <td>
                              {{
                                field.current ? (field.current | dateFormat: 'date' : undefined : true : 'UTC') : '—'
                              }}
                            </td>
                          } @else {
                            <td>{{ field.saved ?? '—' }}</td>
                            <td>{{ field.current ?? '—' }}</td>
                          }
                        </tr>
                      }
                    </tbody>
                  </table>
                }
              }
            </details>
          }
          @if (confirmation() === row.version) {
            <section aria-label="Confirm restore">
              <p>Only the contact's details will change. Ownership, access and groups stay the same.</p>
              @if (held()) {
                <p role="status">Save or cancel your changes first.</p>
              }
              <button type="button" #restoreCancel [disabled]="busy()" (click)="cancel()">Cancel</button>
              <button type="button" [disabled]="busy() || held() || !canEdit()" (click)="confirmRestore()">
                Confirm restore
              </button>
            </section>
          }
        </li>
      }
    </ul>
    @if (total() > 25 || page()) {
      <nav aria-label="Version pages">
        <button type="button" [disabled]="page() === 0 || loading() || busy()" (click)="previous()">Previous</button>
        <button type="button" [disabled]="(page() + 1) * 25 >= total() || loading() || busy()" (click)="next()">
          Next
        </button>
      </nav>
    }
  </section>
}
